import { Suspense, useEffect, useRef, useState, type ComponentType } from 'react';
import { BrowserRouter, Route, Navigate, useLocation, createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  getUser,
  getUserRoles,
  handleCallback,
  login,
  isAuthenticated,
  userManager,
} from './services/auth';
import { trackLoginOnce } from './services/login-tracking';
import ErrorBoundary from './components/common/ErrorBoundary';
import { ToastProvider } from './components/common/ToastProvider';
import Loading from './components/common/Loading';
import { useTranslation } from 'react-i18next';
import { rolesResultFor, useUserRoles } from './hooks/useUserRoles';
import ModuleGuard from './components/common/ModuleGuard';
import {
  RoleRoute,
  SuperadminRoute,
  TenantAdminRoute,
  canUseConsole,
  canUseEnablementPages,
  canUseExecutiveCockpit,
  canUseFleetConsole,
  canUseTenantFleetView,
  prefersFleetConsole,
} from './components/common/RoleRoute';
import ConsoleAccessGuard from './components/common/ConsoleAccessGuard';
import RegionGuard from './components/common/RegionGuard';
import PluginGuard from './components/common/PluginGuard';
import { lazyWithReload } from './utils/lazyWithReload';

// Import i18n configuration
import './i18n';

const lazyPage = <T extends ComponentType>(
  loader: () => Promise<{ default: T }>,
  key: string,
) => lazyWithReload(loader, key);

/**
 * The console has one layout and one navigation (NAV_TREE in
 * components/operator/lib.tsx) for every user (D18, UX audit R-16). The v1/v2
 * layout version (tenant default + per-user override, migration 335) only
 * switches the portal; the console no longer reads it.
 */
const Layout = lazyPage(() => import('./components/common/V2Layout'), 'v2-layout');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'dashboard-page');
const LiveDashboardPage = lazyPage(() => import('./pages/LiveDashboardPage'), 'live-dashboard-page');
const AgentsPage = lazyPage(() => import('./pages/AgentsPage'), 'agents-page');
const ApiKeysPage = lazyPage(() => import('./pages/ApiKeysPage'), 'api-keys-page');
const TenantsPage = lazyPage(() => import('./pages/TenantsPage'), 'tenants-page');
const UsersPage = lazyPage(() => import('./pages/UsersPage'), 'users-page');
const AuditLogsPage = lazyPage(() => import('./pages/AuditLogsPage'), 'audit-logs-page');
const TenderPage = lazyPage(() => import('./pages/TenderPage'), 'tender-page');
const TenderCaseDetailPage = lazyPage(() => import('./pages/TenderCaseDetailPage'), 'tender-case-detail-page');
// May 2026 design rollout: DocumentsPageV2 is the new shelf/inspector layout.
// The legacy DocumentsPage stays at `./pages/DocumentsPage` (unused) so its
// modal stack can be extracted in a follow-up commit.
const DocumentsPage = lazyPage(() => import('./pages/DocumentsPageV2'), 'documents-page');
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'), 'settings-page');
const GdprPage = lazyPage(() => import('./pages/GdprPage'), 'gdpr-page');
const ConsentManagementPage = lazyPage(() => import('./pages/ConsentManagementPage'), 'consent-management-page');
const NewsletterPage = lazyPage(() => import('./pages/NewsletterPage'), 'newsletter-page');
const AvvCheckPage = lazyPage(() => import('./pages/AvvCheckPage'), 'avv-check-page');
const RegulationsPage = lazyPage(() => import('./pages/RegulationsPage'), 'regulations-page');
const PoliciesPage = lazyPage(() => import('./pages/PoliciesPage'), 'policies-page');
const GovernancePage = lazyWithReload(() => import('./pages/GovernancePage'), 'governance-page');
const ActionCardsPage = lazyPage(() => import('./pages/ActionCardsPage'), 'action-cards-page');
const DocsPage = lazyPage(() => import('./pages/DocsPage'), 'docs-page');
const NotificationsPage = lazyPage(() => import('./pages/NotificationsPage'), 'notifications-page');
const KnowledgeGraphPage = lazyPage(() => import('./pages/KnowledgeGraphPage'), 'knowledge-graph-page');
const KnowledgeManagementPage = lazyPage(() => import('./pages/KnowledgeManagementPage'), 'knowledge-management-page');
const ComplianceCalibrationPage = lazyPage(() => import('./pages/ComplianceCalibrationPage'), 'compliance-calibration-page');
const ComplianceChainPage = lazyPage(() => import('./pages/ComplianceChainPage'), 'compliance-chain-page');
const ActiveLearningPage = lazyPage(() => import('./pages/ActiveLearningPage'), 'active-learning-page');
const MemoryPage = lazyPage(() => import('./pages/MemoryPage'), 'memory-page');
const IntegrationsPage = lazyPage(() => import('./pages/IntegrationsPage'), 'integrations-page');
const GuardrailsPage = lazyPage(() => import('./pages/GuardrailsPage'), 'guardrails-page');
const DataProtectionPage = lazyPage(() => import('./pages/DataProtectionPage'), 'data-protection-page');
const LearningMetricsPage = lazyPage(() => import('./pages/LearningMetricsPage'), 'learning-metrics-page');
const WebAnalyticsPage = lazyPage(() => import('./pages/WebAnalyticsPage'), 'web-analytics-page');
const SocialFeedbackPage = lazyPage(() => import('./pages/SocialFeedbackPage'), 'social-feedback-page');
const PoaPage = lazyPage(() => import('./pages/PoaPage'), 'poa-page');
// GovernanceAutopilotPage is now embedded as a tab within GovernancePage
const XaiFeedbackPage = lazyPage(() => import('./pages/XaiFeedbackPage'), 'xai-feedback-page');
const PluginsPage = lazyPage(() => import('./pages/PluginsPage'), 'plugins-page');
const DeepResearchPage = lazyPage(() => import('./pages/DeepResearchPage'), 'deep-research-page');
const TalentSourcingPage = lazyPage(() => import('./pages/TalentSourcingPage'), 'talent-sourcing-page');
// May 2026 design rollout: SecurityScannerPageV2 is the new dark security-firm
// shell with Pro/Client-Briefing mode toggle and the dense Cockpit layout.
// Legacy SecurityScannerPage stays on disk for tab-component reference.
const SecurityScannerPage = lazyPage(() => import('./pages/SecurityScannerPageV2'), 'security-scanner-page');
// May 2026 design rollout: ComplianceScannerPageV2 is the new persona-aware
// shell with Owner/Consultant modes, VerdictDial + ActionCard stack, and
// links to the new Controls-Editor + Report-Editor surfaces. Legacy
// ComplianceScannerPage stays mounted at /compliance-scanner-legacy so the
// heavy data tabs (Scans / Drift / Templates / Settings) remain accessible.
const ComplianceScannerPage = lazyPage(() => import('./pages/ComplianceScannerPageV2'), 'compliance-scanner-page');
const ComplianceScannerLegacy = lazyPage(() => import('./pages/ComplianceScannerPage'), 'compliance-scanner-legacy');
const MaRiskProfilePage = lazyPage(() => import('./pages/MaRiskProfilePage'), 'marisk-profile-page');
// May 2026 design rollout — new Controls/Report editors + adaptive Mein Cockpit.
const ControlsEditorPage = lazyPage(() => import('./pages/ControlsEditorPage'), 'controls-editor-page');
const ReportEditorPage = lazyPage(() => import('./pages/ReportEditorPage'), 'report-editor-page');
const MeinCockpitPage = lazyPage(() => import('./pages/MeinCockpitPage'), 'mein-cockpit-page');
const MCPServersPage = lazyPage(() => import('./pages/MCPServersPage'), 'mcp-servers-page');
const MultimodalHubPage = lazyPage(() => import('./pages/MultimodalHubPage'), 'multimodal-hub-page');
const PluginConsolePage = lazyPage(() => import('./pages/PluginConsolePage'), 'plugin-console-page');
const PQPPage = lazyPage(() => import('./pages/PQPPage'), 'pqp-page');
const WorkflowBuilderPage = lazyPage(() => import('./pages/WorkflowBuilderPage'), 'workflow-builder-page');
const SkillsStudioPage = lazyPage(() => import('./pages/SkillsStudioPage'), 'skills-studio-page');
const IamPage = lazyPage(() => import('./pages/IamPage'), 'iam-page');
const EuAiActPage = lazyPage(() => import('./pages/EuAiActPage'), 'eu-ai-act-page');
const ExecutiveCockpitPage = lazyPage(() => import('./pages/ExecutiveCockpitPage'), 'executive-cockpit-page');
const FleetControlPage = lazyPage(() => import('./pages/FleetControlPage'), 'fleet-control-page');
const EnablementPathwaysPage = lazyPage(() => import('./pages/EnablementPathwaysPage'), 'enablement-pathways-page');
const AIGovernancePage = lazyPage(() => import('./pages/AIGovernancePage'), 'ai-governance-page');
const FleetInstanceDetailPage = lazyPage(() => import('./pages/FleetInstanceDetailPage'), 'fleet-instance-detail-page');
const FleetStatusPage = lazyPage(() => import('./pages/FleetStatusPage'), 'fleet-status-page');
const ReviewQueuePage = lazyPage(() => import('./pages/ReviewQueuePage'), 'review-queue-page');
const NotFoundPage = lazyPage(() => import('./pages/NotFoundPage'), 'not-found-page');
const TenantInactivePage = lazyPage(() => import('./pages/TenantInactivePage'), 'tenant-inactive-page');

// May 2026 design rollout — strict-port operator pages
const OperatorOverviewPage = lazyPage(() => import('./pages/OperatorOverviewPage'), 'operator-overview-page');
const TenantsPageV2 = lazyPage(() => import('./pages/TenantsPageV2'), 'tenants-page-v2');
const RoutingPage = lazyPage(() => import('./pages/RoutingPage'), 'routing-page');
const AdminKnowledgeGraphPage = lazyPage(() => import('./pages/AdminKnowledgeGraphPage'), 'admin-knowledge-graph-page');
const FinanceCorpusPage = lazyPage(() => import('./pages/FinanceCorpusPage'), 'finance-corpus-page');
const EDPBGuidancePage = lazyPage(() => import('./pages/EDPBGuidancePage'), 'edpb-guidance-page');
const PartnerOperationsPage = lazyPage(() => import('./pages/PartnerOperationsPage'), 'partner-operations-page');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Without this every remount refetches, so navigating back to a page
      // shows a spinner over data that is seconds old. Pages needing fresher
      // data override staleTime on their own query.
      staleTime: 30_000,
    },
  },
});

/**
 * `/fleet-status` is the tenant-scoped customer view. A superadmin's home
 * tenant usually owns no edge installations, so that view is legitimately
 * empty, and a fleet viewer is refused by the customer API. Both go to the
 * platform-wide console instead of issuing customer-scoped requests.
 */
function FleetStatusRoute() {
  const roles = useUserRoles();
  if (roles.isLoading) return <Loading />;
  if (prefersFleetConsole(roles)) return <Navigate to="/fleet" replace />;
  return (
    <RoleRoute allow={canUseTenantFleetView} reason="tenantAdmin">
      <FleetStatusPage />
    </RoleRoute>
  );
}

/**
 * The dashboard reports on a tenant (usage, costs, people). Fleet operators
 * without a tenant role use the console for the fleet only, so they start
 * there; /fleet-status forwards fleet viewers on to the fleet console.
 */
function DashboardRoute() {
  const roles = useUserRoles();
  if (roles.isLoading) return <Loading />;
  if (!roles.isTenantAdmin) return <Navigate to="/fleet-status" replace />;
  return <DashboardPage />;
}

/**
 * OAuth Callback Handler Component
 * Handles the redirect from the OIDC provider after authentication
 */
function CallbackHandler({ onSuccess }: { onSuccess: () => void }) {
  const location = useLocation();
  const handledCallbackKeyRef = useRef<string | null>(null);
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [callbackErrorDescription, setCallbackErrorDescription] = useState<string | null>(null);
  // Initialize isProcessing to true if URL has error or code params
  // This prevents flash of error UI during initial mount
  const [isProcessing, setIsProcessing] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('error') || params.has('code');
  });

  useEffect(() => {
    // Check if this is an OAuth callback (has code parameter)
    const params = new URLSearchParams(location.search);
    const callbackKey = `${location.pathname}${location.search}`;

    // If the provider returned an error, show it instead of redirecting in a loop.
    if (params.has('error')) {
      setCallbackError(params.get('error'));
      setCallbackErrorDescription(params.get('error_description'));
      // Add small delay before showing error to prevent flash
      setTimeout(() => setIsProcessing(false), 100);
      return;
    }

    if (params.has('code')) {
      if (handledCallbackKeyRef.current === callbackKey) {
        return;
      }

      handledCallbackKeyRef.current = callbackKey;
      setIsProcessing(true);
      handleCallback()
        .then((user) => {
          // Return to the deep link the user originally requested (carried in
          // the OIDC state by login()), falling back to /dashboard.
          const returnUrl = (user.state as { returnUrl?: string } | undefined)?.returnUrl;
          const target =
            returnUrl && returnUrl !== '/' && !returnUrl.startsWith('/callback')
              ? returnUrl
              : '/dashboard';
          window.history.replaceState({}, document.title, target);
          setIsProcessing(false);
          onSuccess();
        })
        .catch(async (error) => {
          console.error('[App] IAM callback error:', error);
          const errorDescription = error instanceof Error ? error.message : 'Unknown error';

          if (/No matching state found in storage/i.test(errorDescription)) {
            const recoveredUser = await getUser().catch(() => null);
            if (recoveredUser && !recoveredUser.expired) {
              console.warn('[App] Recovered IAM session after duplicate callback');
              window.history.replaceState({}, document.title, '/dashboard');
              setIsProcessing(false);
              onSuccess();
              return;
            }

            const retryKey = `iam-callback-retry:${callbackKey}`;
            if (window.sessionStorage.getItem(retryKey) !== '1') {
              console.warn('[App] Restarting IAM login after stale callback state');
              window.sessionStorage.setItem(retryKey, '1');
              await userManager.clearStaleState().catch(() => {});
              await userManager.removeUser().catch(() => {});
              window.history.replaceState({}, document.title, '/');
              await login();
              return;
            }
          }

          // Avoid an infinite redirect loop; surface the error and allow a manual retry.
          handledCallbackKeyRef.current = null;
          setIsProcessing(false);
          setCallbackError('callback_failed');
          setCallbackErrorDescription(errorDescription);
        });
    }
  }, [location.pathname, location.search, onSuccess]);

  // Only show error UI if processing is complete AND there's an error
  if (callbackError && !isProcessing) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Login failed</h2>
        <p>
          Error: <code>{callbackError}</code>
        </p>
        {callbackErrorDescription ? (
          <p style={{ maxWidth: 800, wordBreak: 'break-word' }}>{callbackErrorDescription}</p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setCallbackError(null);
            setCallbackErrorDescription(null);
            // Best-effort cleanup before retry.
            userManager.clearStaleState().catch(() => {});
            userManager.removeUser().catch(() => {});
            login();
          }}
          style={{ marginTop: 12 }}
        >
          Retry login
        </button>
      </div>
    );
  }

  return <Loading />;
}

function LegacyIdentityProvidersRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: '/integrations/identity-providers',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function AdminRoutes() {
  const [router] = useState(() => createBrowserRouter(createRoutesFromElements(
    <>
                <Route path="/" element={<ConsoleAccessGuard><Layout /></ConsoleAccessGuard>}>
                  {/* May 2026 V2 layout — AdminNav + AdminTopBar + BottomNav */}
                  <Route path="operator/overview" element={<SuperadminRoute><OperatorOverviewPage /></SuperadminRoute>} />
                  <Route path="operator/tenants"  element={<SuperadminRoute><TenantsPageV2 /></SuperadminRoute>} />
                  <Route path="operator/routing"  element={<SuperadminRoute><RoutingPage /></SuperadminRoute>} />
                  <Route path="operator/kg"       element={<SuperadminRoute><AdminKnowledgeGraphPage /></SuperadminRoute>} />
                  {/* Finance Corpus is a plugin; the operator path stays as a redirect for bookmarks. */}
                  <Route path="operator/finance-corpus" element={<Navigate to="/plugins/finance-corpus" replace />} />
                  {/* allowPrivilegedBypass={false}: the module switch has to bind the
                      superadmin too, since that is the only role this route serves. */}
                  <Route path="operator/partners" element={<SuperadminRoute><PartnerOperationsPage /></SuperadminRoute>} />
                  <Route path="operator/edpb-guidance" element={<SuperadminRoute><ModuleGuard moduleKey="edpb_guidance" allowPrivilegedBypass={false}><EDPBGuidancePage /></ModuleGuard></SuperadminRoute>} />

                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardRoute />} />
                  <Route path="live" element={<TenantAdminRoute><LiveDashboardPage /></TenantAdminRoute>} />
                  <Route
                    path="governance/*"
                    element={
                      <ModuleGuard moduleKey="compliance">
                        <SuperadminRoute>
                          <GovernancePage />
                        </SuperadminRoute>
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="guardrails/*"
                    element={
                      <ModuleGuard moduleKey="compliance">
                        <SuperadminRoute>
                          <GuardrailsPage />
                        </SuperadminRoute>
                      </ModuleGuard>
                    }
                  />
                  {/* Input redaction is a platform data-protection control, not a
                      compliance-module feature (owner decision 27.10.2026). */}
                  <Route path="data-protection" element={<TenantAdminRoute><DataProtectionPage /></TenantAdminRoute>} />
                  <Route path="agents/*" element={<TenantAdminRoute><AgentsPage /></TenantAdminRoute>} />
                  <Route
                    path="multimodal-hub"
                    element={
                      <SuperadminRoute>
                        <MultimodalHubPage />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="workflow-builder/*"
                    element={
                      <TenantAdminRoute>
                        <WorkflowBuilderPage />
                      </TenantAdminRoute>
                    }
                  />
                  <Route
                    path="skills-studio/*"
                    element={
                      <ModuleGuard moduleKey="knowledge_management">
                        <TenantAdminRoute>
                          <SkillsStudioPage />
                        </TenantAdminRoute>
                      </ModuleGuard>
                    }
                  />
                  <Route path="api-keys/*" element={<TenantAdminRoute><ApiKeysPage /></TenantAdminRoute>} />
                  <Route
                    path="tenants/*"
                    element={
                      <SuperadminRoute>
                        <TenantsPage />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="users/*"
                    element={
                      <TenantAdminRoute>
                        <UsersPage />
                      </TenantAdminRoute>
                    }
                  />
                  <Route path="audit-logs" element={<TenantAdminRoute><AuditLogsPage /></TenantAdminRoute>} />
                  <Route path="tender" element={<TenantAdminRoute><ModuleGuard moduleKey="tender" allowPrivilegedBypass={false}><TenderPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="tender/:caseId" element={<TenantAdminRoute><ModuleGuard moduleKey="tender" allowPrivilegedBypass={false}><TenderCaseDetailPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="documents" element={<TenantAdminRoute><DocumentsPage /></TenantAdminRoute>} />
                  <Route path="regulations/*" element={<TenantAdminRoute><ModuleGuard moduleKey="compliance"><RegulationsPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="regulatory-radar" element={<ModuleGuard moduleKey="compliance"><Navigate to="/regulations/radar" replace /></ModuleGuard>} />
                  <Route path="policies/*" element={<TenantAdminRoute><ModuleGuard moduleKey="compliance"><PoliciesPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="gdpr" element={<TenantAdminRoute><RegionGuard euOnly><ModuleGuard moduleKey="compliance"><GdprPage /></ModuleGuard></RegionGuard></TenantAdminRoute>} />
                  <Route path="consent-management" element={<TenantAdminRoute><ConsentManagementPage /></TenantAdminRoute>} />
                  <Route path="newsletter" element={<TenantAdminRoute><NewsletterPage /></TenantAdminRoute>} />
                  <Route path="avv-check" element={<TenantAdminRoute><ModuleGuard moduleKey="compliance"><AvvCheckPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="action-cards" element={<TenantAdminRoute><ActionCardsPage /></TenantAdminRoute>} />
                  <Route
                    path="settings"
                    element={
                      <SuperadminRoute>
                        <SettingsPage />
                      </SuperadminRoute>
                    }
                  />
                  <Route path="docs" element={<DocsPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="knowledge-graph" element={<TenantAdminRoute><KnowledgeGraphPage /></TenantAdminRoute>} />
                  <Route path="knowledge" element={<TenantAdminRoute><KnowledgeManagementPage /></TenantAdminRoute>} />
                  <Route path="compliance-calibration" element={<TenantAdminRoute><ModuleGuard moduleKey="compliance"><ComplianceCalibrationPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="executive-cockpit" element={<RoleRoute allow={canUseExecutiveCockpit} reason="cockpit"><ModuleGuard moduleKey="executive_cockpit"><ExecutiveCockpitPage /></ModuleGuard></RoleRoute>} />
                  {/* The cockpit is one route now; the six former sub-pages are
                      sections on it. Bookmarks land on the matching anchor. */}
                  <Route path="executive-cockpit/owners" element={<Navigate to="/executive-cockpit#owners" replace />} />
                  <Route path="executive-cockpit/digests" element={<Navigate to="/executive-cockpit#digests" replace />} />
                  <Route path="executive-cockpit/costs" element={<Navigate to="/executive-cockpit#costs" replace />} />
                  <Route path="executive-cockpit/business-units" element={<Navigate to="/executive-cockpit#bus" replace />} />
                  <Route path="executive-cockpit/parent-links" element={<Navigate to="/executive-cockpit#group" replace />} />
                  <Route path="executive-cockpit/group" element={<Navigate to="/executive-cockpit#group" replace />} />
                  <Route path="compliance-chain" element={<TenantAdminRoute><ModuleGuard moduleKey="compliance"><ComplianceChainPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="active-learning" element={<TenantAdminRoute><ActiveLearningPage /></TenantAdminRoute>} />
                  <Route path="memory" element={<TenantAdminRoute><MemoryPage /></TenantAdminRoute>} />
                  <Route path="learning-metrics" element={<TenantAdminRoute><LearningMetricsPage /></TenantAdminRoute>} />
                  <Route path="web-analytics" element={<SuperadminRoute><WebAnalyticsPage /></SuperadminRoute>} />
                  <Route path="xai-feedback" element={<TenantAdminRoute><XaiFeedbackPage /></TenantAdminRoute>} />
                  <Route path="poa" element={<SuperadminRoute><PoaPage /></SuperadminRoute>} />
                  <Route path="iam/*" element={<SuperadminRoute><IamPage /></SuperadminRoute>} />
                  <Route path="plugins/social-feedback/*" element={<TenantAdminRoute><PluginGuard pluginKey="social-feedback"><SocialFeedbackPage /></PluginGuard></TenantAdminRoute>} />
                  <Route
                    path="social-feedback/*"
                    element={<Navigate to="/plugins/social-feedback" replace />}
                  />
                  <Route
                    path="integrations/*"
                    element={
                      <SuperadminRoute>
                        <IntegrationsPage />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="identity-providers/*"
                    element={
                      <SuperadminRoute>
                        <LegacyIdentityProvidersRedirect />
                      </SuperadminRoute>
                    }
                  />
                  <Route
                    path="governance-autopilot/*"
                    element={<ModuleGuard moduleKey="compliance"><Navigate to="/governance/autopilot" replace /></ModuleGuard>}
                  />
                  <Route path="eu-ai-act/*" element={<TenantAdminRoute><RegionGuard euOnly><ModuleGuard moduleKey="aiact"><EuAiActPage /></ModuleGuard></RegionGuard></TenantAdminRoute>} />
                  <Route path="plugins/pqp/*" element={<TenantAdminRoute><PluginGuard pluginKey="pqp"><PQPPage /></PluginGuard></TenantAdminRoute>} />
                  <Route path="plugins/deep-research/*" element={<TenantAdminRoute><PluginGuard pluginKey="deep-research"><DeepResearchPage /></PluginGuard></TenantAdminRoute>} />
                  <Route path="plugins/talent-sourcing/*" element={<TenantAdminRoute><PluginGuard pluginKey="talent-sourcing"><TalentSourcingPage /></PluginGuard></TenantAdminRoute>} />
                  {/* Curation is superadmin-only; the module is a relevance gate that
                      hard-enforces, so a tenant with it switched off has no way in. */}
                  <Route
                    path="plugins/finance-corpus/*"
                    element={
                      <ModuleGuard moduleKey="finance_corpus" allowPrivilegedBypass={false}>
                        <SuperadminRoute><FinanceCorpusPage /></SuperadminRoute>
                      </ModuleGuard>
                    }
                  />
                  <Route path="plugins/security-scanner/*" element={<TenantAdminRoute><ModuleGuard moduleKey="it_security" allowPrivilegedBypass><SecurityScannerPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="compliance-scanner/*" element={
                    <TenantAdminRoute>
                      <ModuleGuard moduleKey="compliance">
                        <PluginGuard pluginKey="compliance-scanner">
                          <ComplianceScannerPage />
                        </PluginGuard>
                      </ModuleGuard>
                    </TenantAdminRoute>
                  } />
                  <Route path="compliance-scanner/marisk-profile" element={
                    <TenantAdminRoute>
                      <ModuleGuard moduleKey="compliance">
                        <PluginGuard pluginKey="compliance-scanner"><MaRiskProfilePage /></PluginGuard>
                      </ModuleGuard>
                    </TenantAdminRoute>
                  } />
                  <Route path="compliance-scanner-legacy/*" element={
                    <TenantAdminRoute>
                      <ModuleGuard moduleKey="compliance">
                        <PluginGuard pluginKey="compliance-scanner">
                          <ComplianceScannerLegacy />
                        </PluginGuard>
                      </ModuleGuard>
                    </TenantAdminRoute>
                  } />
                  <Route path="compliance-scanner/controls-editor/:customId?" element={
                    <TenantAdminRoute>
                      <ModuleGuard moduleKey="compliance">
                        <PluginGuard pluginKey="compliance-scanner">
                          <ControlsEditorPage />
                        </PluginGuard>
                      </ModuleGuard>
                    </TenantAdminRoute>
                  } />
                  <Route path="compliance-scanner/report-editor/:templateId?" element={
                    <TenantAdminRoute>
                      <ModuleGuard moduleKey="compliance">
                        <PluginGuard pluginKey="compliance-scanner">
                          <ReportEditorPage />
                        </PluginGuard>
                      </ModuleGuard>
                    </TenantAdminRoute>
                  } />
                  <Route path="mein-cockpit" element={
                    <TenantAdminRoute>
                      <ModuleGuard moduleKey="compliance">
                        <PluginGuard pluginKey="compliance-scanner">
                          <MeinCockpitPage />
                        </PluginGuard>
                      </ModuleGuard>
                    </TenantAdminRoute>
                  } />
                  <Route path="plugins/compliance-scanner/*" element={
                    <ModuleGuard moduleKey="compliance">
                      <PluginGuard pluginKey="compliance-scanner">
                        <Navigate to="/compliance-scanner" replace />
                      </PluginGuard>
                    </ModuleGuard>
                  } />
                  <Route path="mcp-servers" element={<TenantAdminRoute><ModuleGuard moduleKey="it_security" allowPrivilegedBypass><MCPServersPage /></ModuleGuard></TenantAdminRoute>} />
                  <Route path="fleet" element={<RoleRoute allow={canUseFleetConsole} reason="fleet"><FleetControlPage /></RoleRoute>} />
                  {/* Workforce compliance (EU AI Act Art. 4) — assignment +
                      evidence ledger. The learner experience lives in the
                      tenant frontend. */}
                  <Route path="enablement" element={<RoleRoute allow={canUseEnablementPages} reason="complianceTeam"><EnablementPathwaysPage /></RoleRoute>} />
                  <Route path="fleet/:instanceId" element={<RoleRoute allow={canUseFleetConsole} reason="fleet"><FleetInstanceDetailPage /></RoleRoute>} />
                  <Route path="ai-governance" element={<TenantAdminRoute><AIGovernancePage /></TenantAdminRoute>} />
                  <Route path="fleet-status" element={<FleetStatusRoute />} />
                  <Route path="review" element={<TenantAdminRoute><ReviewQueuePage /></TenantAdminRoute>} />

                  <Route path="plugins/:pluginId/*" element={<TenantAdminRoute><PluginConsolePage /></TenantAdminRoute>} />
                  <Route path="plugins" element={<TenantAdminRoute><PluginsPage /></TenantAdminRoute>} />
                  <Route
                    path="security-scanner/*"
                    element={<ModuleGuard moduleKey="it_security" allowPrivilegedBypass><Navigate to="/plugins/security-scanner" replace /></ModuleGuard>}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                {/* Callback route for OAuth redirect - handled by CallbackHandler above */}
                <Route path="callback" element={<Navigate to="/dashboard" replace />} />
                <Route path="tenant-inactive" element={<TenantInactivePage />} />
</>
  )));
  return <RouterProvider router={router} />;
}

function NotAuthenticated() {
  const { t } = useTranslation();
  return (
    <div role="alert" style={{ padding: 24, maxWidth: 560 }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>
        {t('auth.notAuthenticatedTitle', 'Sie sind nicht angemeldet')}
      </h1>
      <p style={{ margin: '0 0 16px' }}>
        {t(
          'auth.notAuthenticatedBody',
          'Die Anmeldung konnte nicht gestartet werden. Bitte melden Sie sich erneut an. Wenn das wiederholt scheitert, wenden Sie sich an den Support.',
        )}
      </p>
      <button
        type="button"
        onClick={() => {
          void login();
        }}
        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid currentColor', cursor: 'pointer' }}
      >
        {t('auth.login', 'Anmelden')}
      </button>
    </div>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  // Detect OAuth callback synchronously to avoid a race where the normal auth
  // init effect triggers a new login redirect before we process the callback.
  const [isCallback, setIsCallback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') || params.has('error');
  });

  useEffect(() => {
    // Skip auth initialization if we're handling a callback
    if (isCallback) {
      return;
    }

    const initAuth = async () => {
      try {
        // Check if user is already authenticated
        const isAuth = await isAuthenticated();
        if (isAuth) {
          const user = await getUser();
          setAuthenticated(true);
          setLoading(false);

          // Make userManager globally available for debugging
          (window as unknown as { agentic360IamAuth: typeof userManager }).agentic360IamAuth = userManager;

          // Roles refused by the console (D22) only see the portal page, so
          // their visit is no console sign-in and sends no console request.
          if (canUseConsole(rolesResultFor(getUserRoles(user)))) {
            trackLoginOnce(user, 'admin-dashboard').catch((error) => {
              console.warn('Failed to track login:', error);
            });
          }
        } else {
          await login();
        }
      } catch (error) {
        console.error('[App] IAM auth initialization error:', error);
        setLoading(false);
        // Try to login on error
        await login();
      }
    };

    initAuth();

    // Token refresh is handled automatically by oidc-client-ts with automaticSilentRenew
    // No manual interval needed
  }, [isCallback]);

  // Handle OAuth callback
  if (isCallback) {
    return (
      <BrowserRouter>
        <CallbackHandler
          onSuccess={() => {
            setIsCallback(false);
            setAuthenticated(true);
            setLoading(false);
          }}
        />
      </BrowserRouter>
    );
  }

  if (loading) {
    return <Loading />;
  }

  if (!authenticated) {
    return <NotAuthenticated />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Suspense fallback={<Loading />}>
              <AdminRoutes />
          </Suspense>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
